// ==========================================================================
// Volver atrás a donde estabas.
//
// Se baja por una lista de cuarenta clientes, se abre el 31, se vuelve: el
// teléfono tiene que dejarte otra vez delante del 31, no arriba del todo. Lo
// que hace cualquier app y cualquier web de toda la vida, y que aquí no
// pasaba: el `scrollBehavior` del enrutador mueve el DOCUMENTO, y en este ERP
// el documento no se desplaza nunca — lo hace el panel del armazón
// (`MAIN.shell__content`, ver AppLayout.vue). Así que el enrutador guardaba y
// devolvía una posición que siempre era 0.
//
// Esto guarda la del panel por ENTRADA DEL HISTORIAL y la devuelve al volver
// (atrás o adelante del navegador, el gesto de deslizar, el «volver» de
// Android). Una navegación nueva (un enlace, un `push`) empieza arriba.
//
// Las tres cosas que lo hacen funcionar y que un intento ingenuo no tiene:
//
//   · La clave es la entrada del historial (`history.state.position` de
//     vue-router y su camino), no la dirección. La misma lista abierta dos
//     veces en la misma sesión son dos entradas con dos posiciones.
//
//   · Se guarda ANTES de irse (`beforeEach`), con la pantalla vieja aún
//     montada. En un `popstate` el navegador ya ha cambiado `history.state`
//     cuando nos enteramos, así que la clave de la que nos vamos es la que se
//     apuntó al llegar (`afterEach`), no la que hay ahora.
//
//   · Se devuelve cuando la pantalla nueva tiene ALTO para ello. Una lista
//     vuelve vacía y carga sus filas por la red: poner el scroll en 1800 sobre
//     una página de 600 lo deja en 600 y la persona aparece en mitad de nada.
//     Se reintenta en cada fotograma hasta que cabe, y se rinde (y pone lo
//     que pueda) a los `patience` ms. Y si la persona toca la pantalla
//     mientras tanto, se deja de insistir: su dedo manda.
//
// Lo que NO toca: el rastro de «de dónde vienes» del botón de volver
// (useReturnTo) va en el `state` de la entrada y no se mira aquí; y las
// pantallas que escriben su propia consulta (`meta.ownsQuery`) no se remontan
// al cambiarla, así que tampoco se mueve su scroll — ver `onViewEntered`.
// ==========================================================================

const MAX_ENTRIES = 60
const PATIENCE_MS = 2500
const USER_INPUT = ['touchstart', 'wheel', 'pointerdown', 'keydown']

/**
 * La clave de una entrada del historial.
 * @param {object|null} state  `history.state` (vue-router guarda `position`)
 * @param {string} fullPath
 */
export function entryKey(state, fullPath) {
  const position = state && typeof state.position === 'number' ? state.position : 'x'
  return `${position}|${fullPath}`
}

/**
 * @param {object} options
 * @param {import('vue-router').Router} options.router
 * @param {() => HTMLElement|null} options.pane           el panel que se desplaza
 * @param {() => object|null} [options.historyState]      `history.state`
 * @param {(cb: () => void) => any} [options.nextFrame]
 * @param {() => number} [options.now]
 * @param {number} [options.patience]
 * @param {(back: boolean) => void} [options.onNavigated]  tras cada navegación
 *        que llega: si fue hacia atrás/adelante del historial (la transición
 *        del armazón desliza hacia un lado o hacia el otro)
 */
export function createScrollMemory({
  router,
  pane,
  historyState = () => window.history.state,
  nextFrame = cb => window.requestAnimationFrame(cb),
  now = () => Date.now(),
  patience = PATIENCE_MS,
  onNavigated = () => {},
}) {
  const positions = new Map()
  let currentKey = null
  let popping = false
  // Lo que toca hacer cuando la pantalla nueva entre: 'top' | 'restore' | null.
  let pending = null
  let lastWasBack = false
  let cancelRestore = null

  function remember(key, top) {
    positions.delete(key)
    positions.set(key, top)
    // Las más viejas fuera: nadie vuelve sesenta pantallas atrás a por su scroll.
    while (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value)
  }

  const stopListening = router.options?.history?.listen?.(() => { popping = true })

  const stopBefore = router.beforeEach(() => {
    const el = pane()
    if (el && currentKey) remember(currentKey, el.scrollTop)
  })

  const stopAfter = router.afterEach((to, _from, failure) => {
    const wasPop = popping
    popping = false
    if (failure) return
    currentKey = entryKey(historyState(), to.fullPath)
    lastWasBack = wasPop
    pending = wasPop ? 'restore' : 'top'
    // Una entrada nueva en una posición que ya tuvo otra (se volvió atrás y se
    // navegó a otro sitio): lo guardado era de la que ya no existe.
    if (!wasPop) positions.delete(currentKey)
    onNavigated(wasPop)
  })

  function stopRestoring() {
    cancelRestore?.()
    cancelRestore = null
  }

  /**
   * La pantalla nueva ya está en el panel. Lo llama el armazón desde el
   * `@enter` de la transición del RouterView, que solo salta cuando la
   * pantalla se REMONTA (cambia su `key`): cambiar la consulta de una pantalla
   * `ownsQuery` no lo dispara y su scroll se queda donde está.
   */
  function onViewEntered() {
    const el = pane()
    const what = pending
    pending = null
    stopRestoring()
    if (!el || !what) return

    const target = what === 'restore' ? positions.get(currentKey) : 0
    if (!target) {
      el.scrollTop = 0
      return
    }

    let gaveUp = false
    const started = now()
    const giveUp = () => { gaveUp = true }
    for (const type of USER_INPUT) el.addEventListener(type, giveUp, { passive: true, once: true })
    cancelRestore = () => {
      gaveUp = true
      for (const type of USER_INPUT) el.removeEventListener(type, giveUp)
    }

    const attempt = () => {
      if (gaveUp) return
      const room = el.scrollHeight - el.clientHeight
      const late = now() - started >= patience
      if (room >= target || late) {
        el.scrollTop = target
        stopRestoring()
        return
      }
      nextFrame(attempt)
    }
    attempt()
  }

  return {
    onViewEntered,
    /** Si la última navegación fue hacia atrás/adelante del historial. */
    wasBack: () => lastWasBack,
    /** Para las pruebas. */
    positions,
    stop() {
      stopRestoring()
      stopListening?.()
      stopBefore()
      stopAfter()
    },
  }
}
